import React, { FC } from 'react';

import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

interface AvailableLanguage {
  href: string;
  label: string;
}

interface Props {
  availableLanguages: AvailableLanguage[];
}

const Overview = () => {
  return (
    <>
      <p>
        When Plaid provides IDV Services to Customers, you may be asked to
        provide information about yourself to us. On behalf of our Customers, we
        carry out cloud-based identity verification, fraud detection, and
        watchlist screening services. The IDV Services collect information from
        you and about you to predict and help to prevent fraudulent activity in
        real time. Plaid then completes our IDV Services and provides the
        results directly to our Customers. At our Customer's request, we provide
        a copy of or transfer the information you share, including your Personal
        Information, for Customer's use and as part of the IDV Services. We may
        transfer and/or provide access for our Customers to download the
        information we collect from you, including your Personal Information,
        per our Customer’s instructions.
      </p>
      <p>
        Our Customers determine the scope of the request for information and
        what personal information about you might be used for analysis with the
        IDV Services. Customers configure the IDV Services to require and use
        certain information to verify your identity. A Customer may configure
        the IDV Services to ask for limited information. If the requested
        information is insufficient to provide verification, at our Customer’s
        instruction, the IDV Services may request additional information to
        complete the verification process.
      </p>
      <p>
        If you are not satisfied with the result of any processing, you should
        direct your query to the Customer.
      </p>
    </>
  );
};

const Information = () => {
  return (
    <>
      <p>
        Depending on the IDV Services requested by our Customers, we may collect
        your Personal Information in the following ways:
      </p>
      <p>
        <strong>Information you provide</strong>: We collect your Personal
        Information when you provide information requested by the Customer to
        us. If you choose not to provide the requested information, we will not
        be able to provide our Customer with the IDV Services. Please contact
        the Customer to discuss alternative identity verification and screening
        options, if available for your region or nationality.
      </p>
      <p>
        Some of the potential information about you that may be requested on
        behalf of our Customers includes contact details, including your name,
        email address, postal address, phone number, date of birth; identity
        documents with your image; your government-assigned identity number,
        such as a social security number; and/or a photograph or video image of
        you, to assess whether a person matches their identity document and
        appears to be a live person.
      </p>
      <p>
        We collect data directly from you through industry-standard techniques
        such as JavaScript code, SDKs, form submissions, image collection, video
        collection, and behaviors using tools our Customers embed on their
        websites or mobile apps (<strong>“Customer Sites”</strong>).
      </p>
      <p>
        <strong>Information used to verify a phone number</strong>: We also may
        provide a two-factor authentication feature to our Customers to help
        provide measures to ensure that the person providing information has
        possession of the phone number associated with that identity. To provide
        this feature for our Customers, we may use your Personal Information
        provided by our Customer or by you to send verification codes to you,
        such as via text messages, which you can then enter to confirm your
        identity when you use a Customer Site or create a new account with a
        Customer.
      </p>
      <p>
        <strong>
          Information we automatically collect when you visit Customer Sites
        </strong>
        : The IDV Services use certain standard tracking technologies to
        automatically collect certain information about your device when you
        interact with and use Customer Sites, including behavioral analytics
        regarding your interaction with the IDV Services, and how often a device
        is used to interact with Customer Sites and the IDV Services. Some of
        this information includes, for example, your IP address and certain
        unique identifiers, may identify a particular computer or device and may
        be considered Personal Information in some jurisdictions, including the
        EU.
      </p>
      <p>
        The types of information we may collect on behalf of our Customers will
        depend on whether you visit a Customer Site via an API interface, an app
        using our SDK, or a webpage, including:
      </p>
      <ul>
        <li>
          <strong>Browser and Device Information</strong>, including device type
          and model, manufacturer, operating system type and version, web
          browser type and version, user agent, telephone carrier name and
          information, time zone, the network connection type, IP address,
          hardware-based identifiers, host name, device identifiers,
          Android/Google Advertising ID, canvas fingerprint, characteristics
          related to emulation or rooted such as if your device is “jailbroken”,
          and app name and version. We also collect character set, host name,
          language, page title and URL, referrer URL, number of fonts, fonts
          hash, number of plugins, plugins hash, screen height and width, color
          depth, platform, cookie footprint, maximum touch points, Java-Enabled,
          session storage, local storage, whether the resolution has been
          tampered, language or OS, whether ad blocking is enabled, whether do
          not track is enabled, flash socket IP and flash identifier. The SDK
          will also collect phone-related metadata such as battery level, device
          properties, and carrier name.
        </li>
        <li>
          <strong>Information about your behavior on Customer's Sites</strong>,
          i.e. information about your activities on Customer’s Sites, session
          ID, session start and stop time, time zone offset, and location
          information which may be general location information inferred from
          your IP address or, in some circumstances, more precise geolocation
          information based on latitude and longitude coordinates. You may be
          able to control the collection of location information through
          particular Customer Sites by changing the preferences on your mobile
          device.
        </li>
      </ul>
      <p>
        <strong>
          How We Use Tracking Technologies to Collect Information about You.
        </strong>{' '}
        We use standard tracking technologies to automatically collect certain
        information from your device and browser when you visit or interact with
        Customer Sites. When you view or use the IDV Services via a Customer
        Site, Plaid servers are notified, and we are able to collect information
        from the browser or application. In addition, we collect data directly
        from you through standard tracking technologies such as JavaScript or
        SDK, which our Customers can embed on their websites.
      </p>
      <p>The types of tracking technologies we may use include:</p>
      <ul>
        <li>
          <strong>JavaScript Code (JS Snippet)</strong>, also called a tag or
          pixel, is a tiny snippet of code inserted into the content of the
          Customer Site. You can disable JavaScript by changing the settings on
          your browser. Information about how to change those settings can
          usually be found on your internet browser provider's website. Because
          Plaid does not control these settings, we encourage you to check the
          information provided on a regular basis to ensure you are aware of any
          relevant changes.
        </li>
        <li>
          <strong>Mobile SDKs (Software Development Kits)</strong>, or blocks of
          code embedded into a Customer Site that allow Plaid to collect certain
          information. You can control the use of certain information Plaid
          collects through the SDK by following the instructions applicable to
          your mobile device operating system. You may also be able to control
          whether and how your mobile device provides location information on
          particular Customer Sites by changing the preferences on your mobile
          device. Because Plaid does not control these settings, we encourage
          you to check the information provided on a regular basis to ensure you
          are aware of any relevant changes.
        </li>
        <li>
          <strong>Canvas Fingerprinting</strong>, a tracking technique that
          allows us to render graphic images from built-in features of HTML5
          Canvas in your browser. The canvas image is often rendered differently
          on different devices because of a number of factors such as, your web
          browser version, operating system and its settings, and installed
          graphics hardware, and allows us to distinguish you from other users.
          If you would like to disable canvas fingerprinting, you may choose to
          disable JavaScript or download a browser extension that blocks canvas
          fingerprinting techniques but doing this may impair the functionality
          of the IDV Services. Because Plaid does not control these settings or
          extensions, we encourage you to check the information provided about
          them on a regular basis to ensure you are aware of any relevant
          changes.
        </li>
        <li>
          <strong>Device Fingerprinting</strong>, a tracking technique that
          allows us to identify you using information related to your device’s
          software and hardware, including components of your canvas fingerprint
          as combined with your device information. Plaid uses device
          fingerprinting to stop fraud and other bad actors, as well as for
          cybersecurity.
        </li>
      </ul>
      <p>
        Some of this information, such as your IP address and certain unique
        identifiers, may identify a particular computer or device and therefore
        may be considered “Personal Information” in some jurisdictions,
        including the EU and California.
      </p>
      <p>
        <strong>
          Information we collect from service providers and third party sources
        </strong>
        : At our Customer’s request and as part of IDV Services for Customers,
        we may combine the information we collect about you with information we
        receive from service providers. For example, we may receive information
        such as whether an IP address is commercial or private, whether a phone
        number is a landline, or whether an email domain is free. Customers may
        also ask us to work with third parties that match information provided
        against third-party sources such as credit header files, mobile account
        records, utility records, motor vehicle records and other reputable
        sources. For certain countries, your mobile carrier may be asked to
        disclose your mobile account details for the purpose of verifying your
        identity, including your name, address, and device details. We may also
        receive information about you from third parties and/or collect
        information about you that is publicly available, on behalf of our
        Customer.
      </p>
      <p>
        <strong>Special categories of personal data.</strong> If our Customers
        require you to provide us with an document that contains your photograph
        or if you need to verify your identity by providing a photograph or
        video image of yourself, these images may reveal special categories of
        data about you, for example, information relating to your health (for
        example, if you wear glasses), your race or ethnicity or relating to
        your religious or political beliefs. We use these special categories of
        personal data only on our Customer's instructions and in accordance with
        any of our Customers' privacy policies and this IDV Privacy Statement
        and for no other reason. Plaid IDV does not sell Personal Information.
      </p>
    </>
  );
};

const Biometric = () => {
  return (
    <>
      <p>
        If our Customer requires you to provide us with an identity document
        that contains an image of your face and a photograph or video image
        (sometimes called a selfie), we may compare your derived facial geometry
        from the image on your identity document to your derived facial geometry
        in the photograph or video image that you provide to determine whether a
        person matches their identity document. We may also use your facial
        geometry scan to detect and prevent fraud, including to determine
        whether the video image is of a live person and/or determining your
        estimated age, if applicable. Your identity document image, photograph,
        or video image, and any derived facial geometry scans from those images,
        may be considered biometric information in some jurisdictions.
      </p>
      <p>
        By using the IDV Services to connect to an app or service provided by
        our Customer, you agree that you have read, understand, and voluntarily
        consent to{' '}
        <Link href='#biometric-policy-and-release'>
          <a>Plaid’s Biometric Policy and Release.</a>
        </Link>{' '}
        and release in favor of Plaid and Plaid’s service providers any claims
        related to the collection, processing, use, disclosure of, or sharing of
        your images, photographs, or video images, including any data derived
        from those images to create facial geometry scans.
      </p>
      <p>
        For more information about our collection, use, processing, disclosure,
        and retention of your image from your identity document, photograph, or
        video image, including any derived facial geometry scans from those
        images that may be considered biometric data in your region, please see{' '}
        <Link href='#biometric-policy-and-release'>
          <a>Plaid’s Biometric Policy and Release.</a>
        </Link>{' '}
      </p>
    </>
  );
};

const UseDisclose = () => {
  return (
    <>
      <p>
        Our Customers may use our IDV Services and we return information to the
        Customer about the likelihood that the person providing the information
        is who they claim to be. We also transfer to Customer and/or provide
        access for Customers to download the information we collect from you,
        including your Personal Information, at our Customer's instruction and
        request.
      </p>
      <p>
        Plaid uses your Personal Information to provide the IDV Services to our
        Customers, and to help our Customers comply with their legal
        obligations. We may also use device information or tracking technologies
        to identify and prevent fraud in the IDV Services. For example, we
        process your Personal Information through our cloud-based identity
        verification service to return match information to our Customers for
        particular events or activities on the Customer Site. In addition, when
        our Customers use two-factor authentication, we process your Personal
        Information, such as your telephone number, to send a verification code
        to you via text message. This helps our Customers who use this feature
        to verify identities by ensuring possession of the phone number
        associated with identities.
      </p>
    </>
  );
};

const Anonymized = () => {
  return (
    <>
      <p>
        We may anonymize, de-identify, or aggregate information to maintain,
        improve, optimize or develop the IDV Services. We may also use
        anonymized, de-identified, or aggregated information to prevent and
        detect fraud in the IDV Services. Plaid IDV never uses your Personal
        Information to maintain, improve, optimize, or develop the IDV Services.
      </p>
    </>
  );
};

const Rights = () => {
  return (
    <>
      <p>
        Depending on your location and subject to applicable law, you may have
        the rights regarding your Personal Information we process on Customer’s
        behalf.{' '}
      </p>
      <p>
        <strong>Data Protection Rights.</strong> Where we act as a data
        processor, sub-processor, or service provider on behalf of our
        Customers, you must direct any request to access your Personal
        Information or to exercise any of your data protection rights to the
        Customer. We will assist our Customers in responding to your request and
        will act on their instructions.
      </p>
      <p>
        Please see Plaid’s End User Privacy Policy at{' '}
        <Link href='https://plaid.com/legal/#consumers'>
          <a>https://plaid.com/legal/#consumers</a>
        </Link>{' '}
        for more details on how we deal with any requests where we are
        responsible for processing any personal data as a data controller.
      </p>
    </>
  );
};

const Security = () => {
  return (
    <>
      <p>
        <strong>Security Safeguards</strong>. We use technical and
        organizational security measures designed to protect Personal
        Information processed as part of the IDV Services against unauthorized
        access, disclosure, alteration, and destruction.{' '}
      </p>
      <p>
        <strong>Data Retention</strong>. Customers control their data retention
        policies and may specify how long to retain your Personal Information.
        When instructed by a Customer to do so, your Personal Information will
        either be deleted or anonymized.
      </p>
    </>
  );
};

const Updates = () => {
  return (
    <>
      <p>
        We may revise the IDV Privacy Statement from time to time in response to
        our Customers requirements for the IDV Services and changing legal,
        technical, or business developments. We will provide any updates on our
        Site and the revised version will be effective when it is posted. If we
        make any material changes to the ways in which we use or share Personal
        Information previously collected from you, we will post the updated
        version here. You can see when this IDV Privacy Statement was last
        updated by checking the “last updated” or “effective” date displayed at
        the top of this page.{' '}
      </p>
    </>
  );
};

const Contacting = () => {
  return (
    <>
      <p>
        Please contact Plaid with any questions or comments about this IDV
        Privacy Statement or our privacy practices at privacy@plaid.com or by
        mail at:
      </p>
      <p>
        Plaid Inc.
        <br />
        Attn: Legal
        <br />
        PO Box 7775 #35278
        <br />
        San Francisco, California 94120-7775
        <br />
        U.S.A.
      </p>
    </>
  );
};

const EUUK = () => {
  return (
    <>
      <p>
        Plaid has appointed Plaid B.V. as its Data Protection Representative for
        the purposes of the GDPR in the EU/EEA and the Data Protection Act 2018
        (as amended) in the UK. If you are an EU/EEA or UK resident and we have
        processed or are processing your Personal Information, you may be
        entitled to exercise your rights under the GDPR.{' '}
      </p>
      <p>
        If our Customer on whose behalf we have processed your Personal
        Information is also located in the EEA or UK, you should contact our
        Customer about your Personal Information instead of Plaid’s Data
        Protection Representative. If you still want to raise a question to
        Plaid, or otherwise exercise your rights in respect of your Personal
        Information, you may do so by:
      </p>
      <ul>
        <li>sending an email to privacy@plaid.com; or</li>
        <li>
          contacting our Data Protection Representative via webform at{' '}
          <Link href='https://my.plaid.com/data-subject-request-form'>
            <a>https://my.plaid.com/data-subject-request-form</a>
          </Link>
          .
        </li>
      </ul>
      <p>You may also mail your inquiry to Plaid at the following addresses:</p>
      <p>
        <em>If you reside in the UK:</em>
      </p>
      <p>
        Plaid Financial Ltd.
        <br />
        Attn: Legal
        <br />
        Techspace Level 4, 25 Worship Street
        <br />
        London, United Kingdom, EC2A 2DX
      </p>
      <p>
        <em>If you reside in the EEA:</em>
      </p>
      <p>
        Plaid B.V.
        <br />
        Attn: Legal
        <br />
        Muiderstraat 1<br />
        1011PZ Amsterdam
        <br />
        The Netherlands
      </p>
      <p>
        On receiving your correspondence, Plaid is likely to request evidence of
        your identity to ensure your Personal Information is not provided to
        anyone other than you.
      </p>
    </>
  );
};

const sections = [
  {
    title: 'Overview',
    content: <Overview />,
  },
  {
    title: 'Information We May Collect',
    content: <Information />,
  },
  {
    title: 'Biometric Information and Release',
    content: <Biometric />,
  },
  {
    title:
      'How We May Use & Disclose Your Personal Information to Provide IDV Services to Customers',
    content: <UseDisclose />,
  },
  {
    title:
      'How We May Use Anonymized or Aggregate Information to Maintain,  Improve, and Develop the IDV Services',
    content: <Anonymized />,
  },
  {
    title: 'Your Rights and Choices',
    content: <Rights />,
  },
  {
    title: 'Security and Retention',
    content: <Security />,
  },
  {
    title: 'Updates to this IDV Privacy Statement',
    content: <Updates />,
  },
  {
    title: 'Contacting Plaid',
    content: <Contacting />,
  },
  {
    title: 'For Residents of the European Union and United Kingdom',
    content: <EUUK />,
  },
];

const EnVersion: FC<Props> = ({ availableLanguages }) => {
  return (
    <>
      <h4
        id='idv-privacy-statement'
        data-magellan-target='idv-privacy-statement'
      >
        IDV Privacy Statement
      </h4>

      <p>Available languages:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Last Updated: December 18, 2024</p>
      <p>
        Plaid Inc. and its subsidiaries, Plaid Financial Ltd. and Plaid, B.V.
        (collectively, “Plaid”, “we”, “our”, or “us”) offer identity
        verification, fraud detection, and watchlist screening services (the
        “IDV Services” or “Services”) globally to companies, firms, and
        financial institutions (“Customers” or “Customer”).
      </p>
      <p>
        You may be asked by one of our Customers to complete various checks
        which use the IDV Services. This IDV privacy statement (“IDV Privacy
        Statement”) explains how your personal data (“Personal Information”) may
        be used when we carry out the IDV Services on behalf of our Customers.
      </p>
      <p>
        Plaid does not knowingly collect, process, use, or share Personal
        Information for consumers under the age of 18. The IDV Services are
        intended only for individuals 18 years or older. If you are under 18,
        please do not use the IDV Services.
      </p>
      <p>
        When we provide IDV Services to our Customers, our Customers determine
        the purpose and means by which Personal Information is processed (and
        our Customers are considered a data controller). We act on our
        Customers’ instructions with respect to how and why your Personal
        Information is processed. Plaid is a data processor or sub-processor
        when it facilitates identity verification, fraud detection, and
        watchlist screening services at the direction of our Customers. Our
        Customers direct us to collect your Personal Information. Plaid acts as
        a processor or sub-processor when directed to collect information from
        you, including Personal Information, and Plaid receives instructions
        from our Customers about whom to collect personal data from and when to
        collect and process Personal Information. We use your Personal
        Information to provide IDV Services to our Customers. We also transfer
        and/or provide access to download the information we collect from you,
        including Personal Information, to Customers at their instruction and
        request.
      </p>
      <p>
        You should always check the privacy policies of our Customers and direct
        any queries and requests to exercise your rights with respect to your
        Personal Information to them.
      </p>
      <p>
        Some of the IDV Services may be accessed directly by you, including
        through our websites that reference this policy (collectively “Sites”).
        In some instances, for example, if you are a Customer or if you wish to
        make an inquiry about the IDV Services, or if you visit our website, we
        may be a data controller. We describe how and why we use personal data
        in these circumstances in Plaid’s applicable privacy policies at{' '}
        <Link href='https://plaid.com/legal/'>
          <a>www.plaid.com/legal/</a>
        </Link>
        .
      </p>
      <p>
        Plaid will not ever sell your Personal Information from the IDV Services
        to third parties. Our Customers decide how we use your Personal
        Information, control their data retention policies, and may specify how
        long to retain Personal Information and whether and when we share the
        information we collect, including your Personal Information, with our
        Customer.
      </p>
      <p>
        Depending on the service required by our Customers, we may process
        Personal Information on our Customers' behalf as described in this IDV
        Privacy Statement, so we encourage you to read it carefully.
      </p>
      <nav>
        <h5>Jump to Section</h5>
        <ol>
          {sections.map((section, index) => {
            return (
              <li key={index}>
                <a href={`#section-${index + 1}`}>{`${section.title}`}</a>
              </li>
            );
          })}
        </ol>
      </nav>
      {sections.map((section, index) => {
        return (
          <div key={index} id={`section-${index + 1}`}>
            <h5>{`${index + 1}. ${section.title}`}</h5>
            {section.content}
          </div>
        );
      })}
      <hr className='section-divider' />
    </>
  );
};

export default EnVersion;
